// Redacta datos sensibles antes de enviar eventos/logs a Sentry (ver docs/Sentry.md §4)

const SENSITIVE_HEADER_NAMES = [
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-auth-token',
  'x-csrf-token',
  'proxy-authorization'
];

const SENSITIVE_KEYS = [
  'password',
  'secret',
  'credentials',
  'accesstoken',
  'refreshtoken',
  'apikey',
  'session',
  'cookie',
  'token',
  'jwt'
];

// [regex, reemplazo] aplicados en orden sobre mensajes de texto libre
const SENSITIVE_MESSAGE_PATTERNS: Array<[RegExp, string]> = [
  [/Bearer\s+[A-Za-z0-9._-]+/gi, 'Bearer [Filtered]'],
  [/([?&](?:api[_-]?key|token)=)[^&\s]+/gi, '$1[Filtered]'],
  [/([?&]password=)[^&\s]+/gi, '$1[Filtered]'],
  [/("(?:password|secret|token)":\s*")[^"]*(")/gi, '$1[Filtered]$2']
];

function isSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase();
  return SENSITIVE_KEYS.some((sensitive) => normalized.includes(sensitive));
}

// Recorre recursivamente objetos/arrays redactando cualquier key sensible
function redactValue(value: unknown, depth = 0): unknown {
  if (depth > 5) return value; // evita recursión infinita en estructuras cíclicas/muy anidadas

  if (Array.isArray(value)) {
    return value.map((item) => redactValue(item, depth + 1));
  }

  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      result[key] = isSensitiveKey(key) ? '[Filtered]' : redactValue(val, depth + 1);
    }
    return result;
  }

  // segunda linea de defensa: si un string suelto (ej. una URL completa en un breadcrumb)
  // trae un patron tipo Bearer/api_key/password, igual se redacta aunque su key no sea sensible
  return typeof value === 'string' ? redactMessage(value) : value;
}

function redactMessage(message: string): string {
  return SENSITIVE_MESSAGE_PATTERNS.reduce(
    (acc, [pattern, replacement]) => acc.replace(pattern, replacement),
    message
  );
}

// Hook beforeSend: redacta headers, cookies, contexts/extra y mensajes de un evento
export function sanitizeSentryEvent(event: any): any {
  if (event.request?.headers) {
    const headers: Record<string, string> = {};
    for (const [key, val] of Object.entries(event.request.headers as Record<string, string>)) {
      headers[key] = SENSITIVE_HEADER_NAMES.includes(key.toLowerCase()) ? '[Filtered]' : val;
    }
    event.request.headers = headers;
  }

  if (event.request?.cookies) {
    event.request.cookies = '[Filtered]'; // nunca enviar cookies completas, aunque no sean sensibles todas
  }

  if (event.contexts) {
    event.contexts = redactValue(event.contexts);
  }

  if (event.extra) {
    event.extra = redactValue(event.extra);
  }

  if (event.message) {
    event.message = redactMessage(event.message);
  }

  if (event.exception?.values) {
    event.exception.values = event.exception.values.map((exceptionValue: any) => ({
      ...exceptionValue,
      value: exceptionValue.value ? redactMessage(exceptionValue.value) : exceptionValue.value
    }));
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb: any) => ({
      ...breadcrumb,
      data: breadcrumb.data ? redactValue(breadcrumb.data) : breadcrumb.data
    }));
  }

  return event;
}

// Hook beforeSendLog: redacta mensaje y atributos de un log estructurado (Sentry.logger)
export function sanitizeSentryLog(log: any): any {
  if (log.message) {
    log.message = redactMessage(log.message);
  }

  if (log.attributes) {
    log.attributes = redactValue(log.attributes);
  }

  return log;
}
